import React, { useState, useEffect, useRef } from 'react';
import { formatCurrency } from '../../lib/format';
import * as Sentry from '@sentry/react';
import {
  X,
  CreditCard,
  AlertCircle,
  CheckCircle,
  Loader2,
  Shield
} from 'lucide-react';
import { loadHyper } from '@juspay-tech/hyper-js';
import { HyperElements, UnifiedCheckout } from '@juspay-tech/react-hyper-js';
import { getCurrentTenantConfig } from '../../config/tenant';
import { apiClient } from '../../lib/api';
import { useEscapeKey } from '../../hooks/useKeyboardShortcuts';
import {
  parseRedirectError,
  parseSDKError,
  formatErrorForDisplay,
  logPaymentError,
  type HyperswitchError
} from '../../lib/hyperswitchErrors';

// The backend holds an atomic per-invoice charge claim (MD-2696) and returns a
// 409 when a concurrent charge attempt is already in flight — e.g. the modal was
// reopened within the claim's 3-minute stale window. This is an EXPECTED transient
// state, not a failure worth reporting to Sentry. Detect it so we can surface the
// server's friendly "try again in a moment" message instead of a generic error
// (and keep these benign 409s out of Sentry).
const PAYMENT_IN_PROGRESS_FALLBACK =
  'A payment for this invoice is already in progress. Please wait a moment and try again.';

function getPaymentInProgressMessage(err: any): string | null {
  if (err?.response?.status !== 409) return null;
  return err?.response?.data?.error || PAYMENT_IN_PROGRESS_FALLBACK;
}

interface InvoicePaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  invoice: {
    _id: string;
    invoiceNumber: string;
    amountDue: number;
    physicianName?: string;
    clinicName?: string;
  };
  /** Whether to save the card for future use (default: true) */
  saveCard?: boolean;
  /** Title for the modal */
  title?: string;
  /** Path to redirect to after payment (default: /admin/invoices for admins) */
  returnPath?: string;
  /** Allow admin to enter a partial payment amount (split payments) */
  allowPartialPayment?: boolean;
}

// Error boundary for Hyperswitch SDK
class HyperswitchErrorBoundary extends React.Component<
  { children: React.ReactNode; onError?: (error: Error) => void },
  { hasError: boolean; errorMessage: string }
> {
  constructor(props: { children: React.ReactNode; onError?: (error: Error) => void }) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, errorMessage: error.message };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Hyperswitch payment form failed to load:', error, errorInfo);
    Sentry.captureException(error, {
      tags: { 'payment.component': 'HyperswitchErrorBoundary' },
      extra: { componentStack: errorInfo.componentStack }
    });
    this.props.onError?.(error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="border border-red-300 bg-red-50 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <h3 className="font-medium text-red-800">Payment Form Error</h3>
          </div>
          <p className="text-sm text-red-700">
            Failed to load payment form. Please try again or contact support.
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}

// Checkout form component
const CheckoutForm: React.FC<{
  invoice: InvoicePaymentModalProps['invoice'];
  saveCard: boolean;
  returnPath: string;
  onPaymentError: (error: HyperswitchError) => void;
  paymentAmount: number;
}> = ({ invoice, saveCard, returnPath, onPaymentError, paymentAmount }) => {
  const tenantConfig = getCurrentTenantConfig();
  const returnUrl = `${window.location.origin}${returnPath}?payment=success&invoice=${invoice.invoiceNumber}`;

  const unifiedCheckoutOptions = {
    layout: { type: 'tabs' as const },
    business: { name: tenantConfig.branding.companyName },
    // Note: 'loader' option belongs in HyperElements options, not here
    // Set card as default payment method to avoid SDK warning
    paymentMethodOrder: ['card', 'google_pay', 'apple_pay'],
    fields: {
      billingDetails: {
        name: 'auto' as const,
        email: 'auto' as const,
        phone: 'auto' as const,
        address: 'auto' as const
      }
    },
    wallets: {
      walletReturnUrl: returnUrl,
      applePay: 'auto' as const,
      googlePay: 'auto' as const
    },
    sdkHandleConfirmPayment: {
      handleConfirm: true,
      buttonText: `Pay $${paymentAmount.toFixed(2)}`,
      confirmParams: {
        return_url: returnUrl,
      },
    },
    // Show saved payment methods
    displaySavedPaymentMethods: true,
    // Show checkbox to save card only if saveCard is true
    displaySavedPaymentMethodsCheckbox: saveCard
  };

  return (
    <div className="space-y-4">
      <style>{`
        /* Hide Hyperswitch branding */
        [class*="powered"],
        [class*="hyperswitch"],
        [class*="Powered"],
        [data-testid*="powered"],
        [class*="footer"],
        [class*="Footer"],
        a[href*="hyperswitch"] {
          display: none !important;
          visibility: hidden !important;
          height: 0 !important;
          opacity: 0 !important;
        }
      `}</style>
      <UnifiedCheckout
        id="invoice-checkout"
        options={unifiedCheckoutOptions}
        onReady={() => {
          console.log('Invoice payment form loaded successfully');
        }}
        onError={(error: any) => {
          const parsedError = parseSDKError(error);
          logPaymentError('UnifiedCheckout', parsedError, {
            invoiceId: invoice._id,
            invoiceNumber: invoice.invoiceNumber
          });
          Sentry.captureException(error, {
            tags: {
              'payment.component': 'UnifiedCheckout',
              'payment.errorCode': parsedError.code
            },
            extra: {
              invoiceNumber: invoice.invoiceNumber,
              parsedError
            }
          });
          onPaymentError(parsedError);
        }}
      />
    </div>
  );
};

export const InvoicePaymentModal: React.FC<InvoicePaymentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  invoice,
  saveCard = true,
  title = 'Pay Invoice',
  returnPath = '/admin/invoices',
  allowPartialPayment = false
}) => {
  const [loading, setLoading] = useState(!allowPartialPayment);
  const [error, setError] = useState<string | null>(null);
  const [paymentError, setPaymentError] = useState<HyperswitchError | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(invoice.amountDue);
  const [amountInputError, setAmountInputError] = useState<string | null>(null);
  const [amountConfirmed, setAmountConfirmed] = useState(!allowPartialPayment);
  const hyperPromiseRef = useRef<Promise<any> | null>(null);
  // Guard: only one create-payment-intent request in flight at a time. The
  // backend now holds an invoice-level charge claim per intent (MD-2696), so a
  // duplicate request (StrictMode double-effect, rapid re-render) would 409.
  const intentRequestInFlightRef = useRef(false);
  const tenantConfig = getCurrentTenantConfig();
  const isPartialPayment = allowPartialPayment && paymentAmount < invoice.amountDue;

  // Get formatted error display
  const errorDisplay = paymentError ? formatErrorForDisplay(paymentError) : null;

  // ESC key to close modal (disabled during payment to prevent accidental closure)
  useEscapeKey(isOpen, onClose, !loading && !paymentSuccess);

  // Load Hyperswitch SDK
  useEffect(() => {
    if (isOpen && !hyperPromiseRef.current) {
      const hyperswitchEnv = tenantConfig.payments.hyperswitch.environment;
      const customBackendUrl = hyperswitchEnv === 'sandbox'
        ? 'https://sandbox.hyperswitch.io'
        : 'https://api.hyperswitch.io';

      hyperPromiseRef.current = loadHyper(tenantConfig.payments.hyperswitch.publishableKey, {
        customBackendUrl
      });
    }
  }, [isOpen, tenantConfig.payments.hyperswitch.publishableKey]);

  // Create payment intent when modal opens (or when amount is confirmed for partial payments)
  useEffect(() => {
    const createPaymentIntent = async () => {
      if (!isOpen || !invoice._id || !amountConfirmed) return;
      if (intentRequestInFlightRef.current) return;
      intentRequestInFlightRef.current = true;

      setLoading(true);
      setError(null);
      setClientSecret(null);
      setPaymentSuccess(false);

      try {
        const response = await apiClient.post(`/invoices/${invoice._id}/create-payment-intent`, {
          saveCard,
          ...(allowPartialPayment && { amount: paymentAmount })
        });

        if (response.success && response.data?.clientSecret) {
          setClientSecret(response.data.clientSecret);
        } else {
          setError(response.error || 'Failed to initialize payment');
        }
      } catch (err: any) {
        const inProgressMsg = getPaymentInProgressMessage(err);
        if (inProgressMsg) {
          // Expected transient 409 from the charge claim — surface the friendly
          // message and skip Sentry (this is not a real failure).
          setError(inProgressMsg);
        } else {
          console.error('Failed to create payment intent:', err);
          Sentry.captureException(err, {
            tags: { 'payment.operation': 'create-invoice-payment-intent' },
            extra: { invoiceId: invoice._id, invoiceNumber: invoice.invoiceNumber }
          });
          setError(err.message || 'Failed to initialize payment');
        }
      } finally {
        intentRequestInFlightRef.current = false;
        setLoading(false);
      }
    };

    createPaymentIntent();
  }, [isOpen, invoice._id, saveCard, amountConfirmed]);

  // Check for payment success from URL params (after redirect)
  // Hyperswitch adds redirect_status param to indicate payment result
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redirectStatus = params.get('redirect_status');
    const invoiceParam = params.get('invoice');

    // Check if this redirect is for our invoice
    if (invoiceParam === invoice.invoiceNumber) {
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);

      if (redirectStatus === 'succeeded') {
        console.log(`✅ Payment succeeded for invoice ${invoice.invoiceNumber} (from redirect_status)`);
        setPaymentSuccess(true);
        // Webhook will update the invoice - notify parent of success
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 2000);
      } else if (redirectStatus === 'failed' || redirectStatus === 'requires_payment_method') {
        // Parse detailed error information from redirect params
        const parsedError = parseRedirectError(params);
        if (parsedError) {
          logPaymentError('Payment redirect', parsedError, {
            invoiceNumber: invoice.invoiceNumber,
            redirectStatus
          });
          Sentry.captureMessage('Payment failed via redirect', {
            level: 'warning',
            tags: {
              'payment.redirectStatus': redirectStatus,
              'payment.errorCode': parsedError.code
            },
            extra: {
              invoiceNumber: invoice.invoiceNumber,
              parsedError
            }
          });
          setPaymentError(parsedError);
        }
        setError(redirectStatus === 'failed' ? 'Payment failed' : 'Payment requires different method');
      }
    }
  }, [invoice.invoiceNumber, onSuccess, onClose]);

  const handleClose = () => {
    setClientSecret(null);
    setError(null);
    setPaymentError(null);
    setLoading(!allowPartialPayment);
    setPaymentAmount(invoice.amountDue);
    setAmountInputError(null);
    setAmountConfirmed(!allowPartialPayment);
    onClose();
  };

  if (!isOpen) return null;


  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 z-[70] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center space-x-2">
              <CreditCard className="h-5 w-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Invoice Summary */}
          <div className="p-4 bg-gray-50 border-b">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-600">Invoice {invoice.invoiceNumber}</p>
                {invoice.physicianName && (
                  <p className="text-xs text-gray-500">{invoice.physicianName}</p>
                )}
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-blue-600">
                  {formatCurrency(invoice.amountDue)}
                </p>
                <p className="text-xs text-gray-500">Amount Due</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-4">
            {/* Amount Selection Step (partial payments only) */}
            {allowPartialPayment && !amountConfirmed && !paymentSuccess ? (
              <div className="space-y-4">
                <label className="block text-sm font-medium text-gray-700">
                  Payment Amount
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    min={1}
                    max={invoice.amountDue}
                    step="0.01"
                    value={paymentAmount}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value);
                      setPaymentAmount(val);
                      if (isNaN(val) || val <= 0) {
                        setAmountInputError('Amount must be greater than $0');
                      } else if (val > invoice.amountDue) {
                        setAmountInputError(`Amount cannot exceed $${invoice.amountDue.toFixed(2)}`);
                      } else {
                        setAmountInputError(null);
                      }
                    }}
                    className={`w-full pl-7 pr-3 py-2 border rounded-lg text-sm ${
                      amountInputError ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
                    } focus:outline-none focus:ring-2`}
                  />
                </div>
                {amountInputError && (
                  <p className="text-xs text-red-600">{amountInputError}</p>
                )}
                {paymentAmount < invoice.amountDue && !amountInputError && paymentAmount > 0 && (
                  <p className="text-xs text-gray-500">
                    Charging ${paymentAmount.toFixed(2)} of ${invoice.amountDue.toFixed(2)} total due
                  </p>
                )}
                <button
                  onClick={() => setAmountConfirmed(true)}
                  disabled={!!amountInputError || !paymentAmount || paymentAmount <= 0}
                  className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                >
                  Continue to Payment — {formatCurrency(paymentAmount)}
                </button>
              </div>
            ) : paymentSuccess ? (
              <div className="text-center py-8">
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <h4 className="text-xl font-semibold text-gray-900 mb-2">Payment Successful!</h4>
                <p className="text-gray-600">
                  {isPartialPayment
                    ? `Partial payment of $${paymentAmount.toFixed(2)} recorded. Remaining: $${(invoice.amountDue - paymentAmount).toFixed(2)}`
                    : `Invoice ${invoice.invoiceNumber} has been paid successfully.`}
                </p>
              </div>
            ) : loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600 mb-4" />
                <p className="text-gray-600">Initializing secure payment...</p>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  {errorDisplay?.title || 'Payment Error'}
                </h4>
                <p className="text-gray-600 mb-2">
                  {errorDisplay?.description || error}
                </p>
                {errorDisplay?.suggestion && (
                  <p className="text-sm text-gray-500 mb-4">
                    {errorDisplay.suggestion}
                  </p>
                )}
                {errorDisplay?.errorCode && (
                  <p className="text-xs text-gray-400 mb-4 font-mono">
                    Error code: {errorDisplay.errorCode}
                  </p>
                )}
                {(!errorDisplay || errorDisplay.canRetry) && (
                  <button
                    onClick={() => {
                      // No-op if an intent request is already in flight (double-click guard)
                      if (intentRequestInFlightRef.current) return;
                      intentRequestInFlightRef.current = true;
                      setError(null);
                      setPaymentError(null);
                      setLoading(true);
                      // Re-trigger payment intent creation
                      const retryPayment = async () => {
                        try {
                          const response = await apiClient.post(`/invoices/${invoice._id}/create-payment-intent`, {
                            saveCard,
                            ...(allowPartialPayment && { amount: paymentAmount })
                          });
                          if (response.success && response.data?.clientSecret) {
                            setClientSecret(response.data.clientSecret);
                          } else {
                            setError(response.error || 'Failed to initialize payment');
                          }
                        } catch (err: any) {
                          const inProgressMsg = getPaymentInProgressMessage(err);
                          if (inProgressMsg) {
                            setError(inProgressMsg);
                          } else {
                            Sentry.captureException(err, {
                              tags: { 'payment.operation': 'retry-invoice-payment-intent' },
                              extra: { invoiceId: invoice._id, invoiceNumber: invoice.invoiceNumber }
                            });
                            setError(err.message || 'Failed to initialize payment');
                          }
                        } finally {
                          intentRequestInFlightRef.current = false;
                          setLoading(false);
                        }
                      };
                      retryPayment();
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Try Again
                  </button>
                )}
              </div>
            ) : clientSecret && hyperPromiseRef.current ? (
              <>
              {allowPartialPayment && isPartialPayment && (
                <div className="flex items-center justify-between mb-3 px-1">
                  <p className="text-sm text-gray-600">
                    Charging <span className="font-semibold">{formatCurrency(paymentAmount)}</span> of {formatCurrency(invoice.amountDue)}
                  </p>
                  <button
                    onClick={() => {
                      setAmountConfirmed(false);
                      setClientSecret(null);
                    }}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Change
                  </button>
                </div>
              )}
              <HyperswitchErrorBoundary onError={(err) => setError(err.message)}>
                <HyperElements
                  options={{
                    publishableKey: tenantConfig.payments.hyperswitch.publishableKey,
                    clientSecret,
                    loader: 'auto',  // Display skeleton UI while loading
                    appearance: {
                      theme: 'default',
                      variables: {
                        colorPrimary: '#3B82F6',
                        colorBackground: '#ffffff',
                        colorText: '#374151',
                        borderRadius: '8px'
                      }
                    }
                  }}
                  hyper={hyperPromiseRef.current}
                >
                  <CheckoutForm
                    invoice={invoice}
                    saveCard={saveCard}
                    returnPath={returnPath}
                    paymentAmount={paymentAmount}
                    onPaymentError={(err) => {
                      setPaymentError(err);
                      setError(err.userMessage);
                    }}
                  />
                </HyperElements>
              </HyperswitchErrorBoundary>
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">
                Unable to load payment form
              </div>
            )}
          </div>

          {/* Footer */}
          {!paymentSuccess && (
            <div className="p-4 border-t bg-gray-50 rounded-b-lg">
              <div className="flex items-center justify-center text-xs text-gray-500">
                <Shield className="h-4 w-4 mr-1" />
                <span>Payments are secure and encrypted</span>
              </div>
              {saveCard && (
                <p className="text-xs text-gray-400 text-center mt-2">
                  Card will be saved for future payments
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoicePaymentModal;
